import type { ElementType, HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType | undefined
  children: ReactNode
  className?: string | undefined
}

/**
 * Typography hierarchy — the six levels used across the app:
 * PageTitle > SectionTitle > CardTitle > Body > Meta > Caption.
 * Prefer these over ad-hoc text-* combinations so hierarchy stays consistent
 * as new pages are added.
 *
 * The default text color only applies when the caller passes no
 * `className` at all — `cn()` is a plain string join with no conflict
 * resolution, so a passed-in color class can't reliably out-rank the
 * component's own default in the compiled stylesheet (Tailwind's cascade
 * order depends on generation order, not on where a class sits in the
 * attribute string). Any caller overriding color is expected to bundle
 * that color into the `className` it passes, same as PageHeader/Analytics
 * already do — see `text-canvas-fg` usages.
 */
export function PageTitle({ as: Tag = 'h1', className, children, ...props }: TextProps) {
  return (
    <Tag className={cn('font-display text-2xl font-bold tracking-tight sm:text-[28px]', className ?? 'text-canvas-fg')} {...props}>
      {children}
    </Tag>
  )
}

export function SectionTitle({ as: Tag = 'h2', className, children, ...props }: TextProps) {
  return (
    <Tag className={cn('font-display text-lg font-semibold tracking-tight', className ?? 'text-canvas-fg')} {...props}>
      {children}
    </Tag>
  )
}

export function CardTitle({ as: Tag = 'h3', className, children, ...props }: TextProps) {
  return (
    <Tag className={cn('font-display text-base font-semibold', className ?? 'text-canvas-fg')} {...props}>
      {children}
    </Tag>
  )
}

export function Body({ as: Tag = 'p', className, children, ...props }: TextProps) {
  return (
    <Tag className={cn('text-sm leading-relaxed', className ?? 'text-canvas-fg/80')} {...props}>
      {children}
    </Tag>
  )
}

export function Meta({ as: Tag = 'p', className, children, ...props }: TextProps) {
  return (
    <Tag className={cn('text-xs font-medium', className ?? 'text-canvas-fg/60')} {...props}>
      {children}
    </Tag>
  )
}

export function Caption({ as: Tag = 'span', className, children, ...props }: TextProps) {
  return (
    <Tag className={cn('text-[11px] font-semibold uppercase tracking-wide', className ?? 'text-canvas-fg/55')} {...props}>
      {children}
    </Tag>
  )
}
